import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  Grid,
  Table,
  TableRow,
  TableBody,
  useTheme,
  TableContainer,
  IconButton,
  Box,
  Alert,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Tablecell,
  applySortFilter,
  calculateAndFormatNoAbsInt,
  findFundAbbById,
  findStrategy,
  formatValue,
  getComparator,
  hashAction,
  hashPosition,
  TABLE_SINGLE_LINE_TRADE as tableHeader,
} from "../../../../component/Methods";
import { selectStrategies, selectFunds, selectSleeveDataStageOne, selectRebalancerOrders, selectAllTrades, selectProcessId, selectLoad,selectActiveRequests } from "../../../../Redux/Reducers/selectors";
import VantageListHead from "../../../../component/ListHead/SignalList";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
} from "../../../../Redux/Reducers/uiSlice";
import Loader from "../../../../component/Loader";
import CrossingTradeRebalancerSkeleton from "../../../../component/spinner/CrossingTradeRebalancerSkeleton";
import { sendWebSocketMessage } from "../../../../hooks/useWebSocket";

const CrossingTradeRebalancer = (props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    title,
    openCrossPopup,
    setOpenCrossPopup,
    setCurrentStep,
    onceClicked,
    showNext,
    rebalanceType,
    rebalancedFunds,
  } = props;
 
  const sleeveDataStageOne = useSelector(selectSleeveDataStageOne);
  const activeRequests = useSelector(selectActiveRequests);   
  const orders = useSelector(selectRebalancerOrders);
  const strategies = useSelector(selectStrategies);
  const funds = useSelector(selectFunds);
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("ticker");
  const [showActionAlert, setShowActionAlert] = useState(false);
  const [actionType, setActionType] = useState("");

  useEffect(() => {
    const filteredOrders = orders?.map((order) => {
      const strategy = findStrategy(order?.strategy, strategies);
      const ticker = order?.ticker.stock
        ? `${order?.ticker?.symbol}/${order?.ticker?.stock}`
        : order?.ticker?.symbol;
      const action = hashAction(order?.action) + hashPosition(order?.position);

      const distribute = Object.entries(order?.distribution || {})?.reduce(
        (acc, [key, value]) => {
          const [_, fundId] = key.split("#");

          if (!acc.fund) {
            acc.fund = findFundAbbById(fundId, funds);
          }

          acc.shareCount = (acc.shareCount || 0) + Number(value);

          return acc;
        },
        {}
      );

      return {
        strategy: strategy,
        ticker: ticker,
        action: action,
        shareCount: distribute?.shareCount || 0,
        fund: distribute?.fund || "",
      };
    });

    const filteredUsers = applySortFilter(
      filteredOrders,
      getComparator(order, orderBy)
    );
    setData(filteredUsers);

    if (filteredOrders && filteredOrders.length > 0) {
      const allActions = filteredOrders.map((item) =>
        item.action.toLowerCase()
      );
      const uniqueActions = [...new Set(allActions)];

      const buyLongSellShort = ["buylong", "sellshort"];
      const sellLongBuyShort = ["selllong", "buyshort"];

      const isAllBuyLongSellShort = uniqueActions.every((action) =>
        buyLongSellShort.includes(action.toLowerCase())
      );
      const isAllSellLongBuyShort = uniqueActions.every((action) =>
        sellLongBuyShort.includes(action.toLowerCase())
      );

      if (isAllBuyLongSellShort) {
        setActionType("buylong_sellshort");
        setShowActionAlert(true);
      } else if (isAllSellLongBuyShort) {
        setActionType("selllong_buyshort");
        setShowActionAlert(true);
      } else {
        setShowActionAlert(false);
        setActionType("");
      }
    } else {
      setShowActionAlert(false);
      setActionType("");
    }
  }, [order, orderBy, orders, sleeveDataStageOne]);

 

  const onNext = () => {
    const rebalancerIds = Object.values(sleeveDataStageOne)?.map(
      (strategy) => strategy?.rebalancer?.rebalancerId
    );
    const isSma = rebalanceType === "all";

    // Define action and message based on SMA status
    const action = isSma
      ? "create_fund_rebalancer_trades"
      : "create_rebalancer_crossing_orders";
    const message = isSma
      ? "Creating Trades"
      : "Creating Crossing Orders";

    // Create base data object
    const baseData = { rebalancerIds };
    const socketData = isSma ? baseData : { ...baseData, rebalancerType: 0 };

    // Send main request
    sendWebSocketMessage(
      JSON.stringify({
        action,
        data: socketData,
      })
    );

    // Dispatch actions
    dispatch(updateLoadingMsg(message));
    dispatch(addActiveRequests(action));
    if(isSma){
      dispatch(addActiveRequests("rebalancer_summary"));
    }
    dispatch(updateLoading(true));

    setOpenCrossPopup(false);
    setCurrentStep(isSma ? 6 : (prev) => prev + 1);
  };
 // Watch for processId and send rebalancer_summary when available
 
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const deleteActivity = (index) => {
    const newData = [...data];
    newData.splice(index, 1);
    setData(newData);
  };
  const handleClose = () => {
    setOpenCrossPopup(false);
  };

  const handleProceed = () => {
    setShowActionAlert(false);
    onNext();
  };

  const handleRestart = () => {
    setShowActionAlert(false);
    setOpenCrossPopup(false);
    if (setCurrentStep) {
      setCurrentStep(0);
    }
  };

  // Check if we're loading crossing orders data
  const isLoadingCrossingOrders = activeRequests.includes(
    "create_rebalancer_orders"
  );

  // Show skeleton while loading
  if (isLoadingCrossingOrders) {
    return (
      <CrossingTradeRebalancerSkeleton
        openCrossPopup={openCrossPopup}
        handleClose={handleClose}
        showNext={showNext}
      />
    );
  }

  return (
    <Dialog
      open={openCrossPopup}
      fullWidth
      sx={{
        "& .css-1t1j96h-MuiPaper-root-MuiDialog-paper": {
          minWidth: "1000px",
          height: "100vh",
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle sx={{ backgroundColor: theme.palette.background.paper }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            component="div"
            align="left"
            sx={{
              flexGrow: 1,
              fontSize: "20px",
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{ color: theme.palette.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ backgroundColor: theme.palette.background.paper }}
      >
        <>
          {showActionAlert && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleProceed}
                    variant="outlined"
                  >
                    Proceed
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleRestart}
                    variant="outlined"
                  >
                    Restart
                  </Button>
                </Box>
              }
            >
              {actionType === "buylong_sellshort"
                ? "All trades are open trades. Do you want to proceed?"
                : "All trades are closed trades. Do you want to proceed?"}
            </Alert>
          )}

          <TableContainer style={{ height: "360px" }}>
            <Table
              size="small"
              stickyHeader
              aria-label="a dense table"
              sx={{
                height: "max-content",
              }}
            >
              <VantageListHead
                order={order}
                orderBy={orderBy}
                headLabel={tableHeader}
                noStatus={true}
                customizeFont={12}
                rowCount={data.length}
                onRequestSort={handleRequestSort}
              />

              <TableBody>
                {data?.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      "& > .MuiTableCell-root": {
                        paddingTop: "1px",
                        paddingBottom: "0px !important",
                        lineHeight: "1.2",
                        "& .MuiInputBase-input": {
                          padding: "0px 0px !important",
                        },
                        "& .MuiIconButton-root": {
                          padding: "0px",
                        },
                      },
                      "&:hover": {
                        background: theme.palette.action.hover,
                        cursor: "pointer",
                      },
                    }}
                  >
                    <Tablecell
                      align="left"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.fund}
                    </Tablecell>

                    <Tablecell
                      align="left"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row.strategy}
                    </Tablecell>
                    <Tablecell
                      align="left"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.action}
                    </Tablecell>
                    <Tablecell
                      align="left"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.ticker?.toUpperCase() || ""}
                    </Tablecell>
                    <Tablecell
                      align="right"
                      sx={{
                        fontSize: "12px",
                        paddingRight: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.shareCount !== undefined && row?.shareCount != null
                        ? row?.shareCount < 0
                          ? `(${Math.abs(row?.shareCount)})`
                          : `${calculateAndFormatNoAbsInt(row?.shareCount)}`
                        : ""}
                    </Tablecell>

                    {/* <Tablecell
                        align="center"
                        sx={{
                          borderRight: "none",
                        }}
                      >
                        <IconButton
                          onClick={() => deleteActivity(index)}
                          aria-label="delete"
                          sx={{
                            height: "15px",
                            width: "15px",
                          }}
                        >
                          <DeleteIcon sx={{ height: "15px", width: "15px" }} />
                        </IconButton>
                      </Tablecell> */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
        {showNext ? (
          <Grid
            item
            xs={12}
            margin="20px 1px 20px 1px"
            display="flex"
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              sx={{
                width: "40%",
              }}
              onClick={onNext}
              disabled={showActionAlert}
            >
             {rebalanceType === "all" ? "Create Trades" : " Create Crossing Orders"}
            </Button>
          </Grid>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default CrossingTradeRebalancer;
